import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { readShopOrder } from '../../api/business/endpoints';
import type { ShopOrderAddress } from '../../api/business/types';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatMoney, formatNumber, formatPercent } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { useLanguage } from '../../i18n/LanguageContext';

import { OrderStatusChip } from './OrderStatusChip';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const HEAD = { color: 'text.secondary', fontWeight: 500 };

/**
 * One order of a linked shop (R0034 Q-04): header, customer and addresses, lines linked to the products they
 * were matched to, totals, every status the shop reported, and the stock changes its lines sent. A document as
 * the shop reported it — read only, in the shop's own order, with no filters or sorting (like an invoice's lines,
 * R0006 D-17).
 */
export const OrderDetailPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { companyId = '', orderId = '' } = useParams();
  const query = useQuery({
    queryKey: ['order', companyId, orderId],
    queryFn: () => readShopOrder(companyId, orderId),
  });
  const detail = query.data;
  const money = (minor: number | null): string =>
    formatMoney(minor, detail?.order.currency ?? 'EUR', language);

  const address = (value: ShopOrderAddress | null) =>
    value === null ? (
      <Typography variant="body2" color="text.secondary">
        {t('order.noAddress')}
      </Typography>
    ) : (
      <Typography variant="body2" component="div">
        {[
          value.name,
          value.company,
          value.street,
          [value.postalCode, value.city].filter(Boolean).join(' '),
          value.country,
          value.phone,
          value.vatId ? t('order.vatId', { vatId: value.vatId }) : null,
        ]
          .filter((line): line is string => Boolean(line))
          .map((line) => (
            <Box key={line}>{line}</Box>
          ))}
      </Typography>
    );

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 1 }}>
        {t('orders.title')}
      </Button>
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      {detail ? (
        <>
          <PageHeader
            title={t('order.title', { reference: detail.order.reference })}
            subtitle={t('order.subtitle', {
              shop: detail.order.linkName,
              platform: t(`platform.${detail.order.platform}` as const),
              id: detail.order.shopOrderId,
            })}
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                {detail.order.fromHistory ? <Chip size="small" label={t('orders.past')} /> : null}
                <OrderStatusChip status={detail.order.status} label={detail.order.statusLabel} />
              </Stack>
            }
          />
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            {detail.order.fromHistory ? t('orders.pastHint') : t('order.oneWay')}
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Section title={t('order.customer')}>
                <Typography variant="body2" component="div">
                  <Box>{detail.customer.name ?? t('common.none')}</Box>
                  {detail.customer.email ? <Box>{detail.customer.email}</Box> : null}
                  {detail.customer.phone ? <Box>{detail.customer.phone}</Box> : null}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                  {t('orders.placedAt')}: {formatDateTime(detail.order.placedAt, language)}
                  <br />
                  {t('order.payment')}: {detail.paymentMethod ?? t('common.none')}
                  <br />
                  {t('order.shipping')}: {detail.shippingMethod ?? t('common.none')}
                </Typography>
              </Section>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Section title={t('order.billing')}>{address(detail.billing)}</Section>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Section title={t('order.delivery')}>{address(detail.delivery)}</Section>
            </Grid>
          </Grid>
          <Paper variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2.5, pt: 2 }}>
              {t('order.lines')}
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={HEAD}>{t('order.item')}</TableCell>
                  <TableCell sx={HEAD} align="right">
                    {t('order.quantity')}
                  </TableCell>
                  <TableCell sx={HEAD} align="right">
                    {t('order.unitNet')}
                  </TableCell>
                  <TableCell sx={HEAD} align="right">
                    {t('order.unitGross')}
                  </TableCell>
                  <TableCell sx={HEAD} align="right">
                    {t('order.vat')}
                  </TableCell>
                  <TableCell sx={HEAD} align="right">
                    {t('order.lineTotal')}
                  </TableCell>
                </TableRow>
                {detail.lines.map((line) => (
                  <TableRow key={line.position}>
                    <TableCell>
                      {line.productId !== null ? (
                        <Link component={RouterLink} to={`/products/${companyId}/${line.productId}`}>
                          {line.name}
                        </Link>
                      ) : (
                        line.name
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {[line.reference, line.ean].filter(Boolean).join(' · ')}
                        {line.reference || line.ean ? ' · ' : ''}
                        {t('order.shopIds', {
                          product: line.shopProductId,
                          variant: line.shopVariantId ? `/${line.shopVariantId}` : '',
                        })}
                        {line.productId === null ? ` · ${t('order.unmatched')}` : ''}
                        {line.productName && line.productName !== line.name ? ` · ${line.productName}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(line.quantity, language)}</TableCell>
                    <TableCell align="right">{money(line.unitPriceNetMinor)}</TableCell>
                    <TableCell align="right">{money(line.unitPriceGrossMinor)}</TableCell>
                    <TableCell align="right">
                      {line.taxRateBasisPoints === null
                        ? t('common.none')
                        : formatPercent(line.taxRateBasisPoints, language)}
                    </TableCell>
                    <TableCell align="right">{money(line.unitPriceGrossMinor * line.quantity)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Section title={t('order.totals')}>
                <Table size="small">
                  <TableBody>
                    {(
                      [
                        ['order.productsTotal', detail.totals.productsGrossMinor],
                        ['order.shippingTotal', detail.totals.shippingGrossMinor],
                        ['order.discountTotal', detail.totals.discountGrossMinor],
                        ['order.totalNet', detail.totals.netMinor],
                        ['order.totalGross', detail.totals.grossMinor],
                        ['order.paid', detail.totals.paidMinor],
                      ] as const
                    ).map(([label, minor]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ color: 'text.secondary', pl: 0 }}>{t(label)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ pr: 0, fontWeight: label === 'order.totalGross' ? 600 : undefined }}
                        >
                          {money(minor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Section>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Section title={t('order.history')}>
                <Stack spacing={1}>
                  {detail.statuses.map((status) => (
                    <Stack key={`${status.at}-${status.status}-${status.label}`} direction="row" spacing={1}>
                      <OrderStatusChip status={status.status} label={status.label} />
                      <Box>
                        <Typography variant="body2">{status.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(status.at, language)}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Section>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Section title={t('order.stockChanges')}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                  {t('order.stockChangesHint')}
                </Typography>
                {detail.stockChanges.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('order.noStockChanges')}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {detail.stockChanges.map((change) => (
                      <Box key={change.key}>
                        <Typography variant="body2">
                          {t(`changeReason.${change.reason}` as const)} ·{' '}
                          <Box
                            component="span"
                            sx={{ color: change.delta > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                          >
                            {change.delta > 0 ? '+' : ''}
                            {formatNumber(change.delta, language)}
                          </Box>
                          {change.outcome ? ` · ${t(`changeOutcome.${change.outcome}` as const)}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(change.receivedAt, language)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Section>
            </Grid>
          </Grid>
        </>
      ) : null}
    </>
  );
};
